import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import {
  type AbstractControl,
  ReactiveFormsModule,
  type ValidationErrors,
  type ValidatorFn,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { ColorPickerComponent } from '@clean-code/shared/components/ui-color-picker';
import { InputTranslationComponent } from '@clean-code/shared/components/ui-translation';
import { VisibleToComponent } from '@clean-code/shared/components/ui-visible-to';
import {
  DashboardFacade,
  type IDashboardListDto,
} from '@clean-code/shared/dashboard/dashboard/domain';
import { FormDirectiveModule } from '@clean-code/shared/directives/ui-form';
import { TenantService } from '@clean-code/shared/util/util-tenant';
import { TranslocoModule } from '@jsverse/transloco';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { FormContainerColDirective } from 'shared/directives/ui-layout';

const DEFAULT_DASHBOARD_COLOR = '#cdcdcd';

@Component({
  selector: 'dashboard-dashboard-edit',
  templateUrl: './dashboard-edit.component.html',
  styleUrls: ['./dashboard-edit.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatSelectModule,
    MatInputModule,
    MatProgressBar,
    TranslocoModule,
    InputTranslationComponent,
    ColorPickerComponent,
    FormDirectiveModule,
    VisibleToComponent,
    FormContainerColDirective,
  ],
})
export class DashboardEditComponent {
  public dialogRef = inject(MatDialogRef<DashboardEditComponent>);
  private tenantService = inject(TenantService);
  private service = inject(DashboardFacade);

  public form = new UntypedFormGroup(
    {
      id: new UntypedFormControl(null),
      name: new UntypedFormControl(null, [
        Validators.required,
        this.requiredTranslationValues(),
      ]),
      description: new UntypedFormControl(null),
      color: new UntypedFormControl(
        DEFAULT_DASHBOARD_COLOR,
        Validators.required,
      ),
      shared: new UntypedFormControl(false),
      visibleTo: new UntypedFormControl(null),
      tenant: new UntypedFormControl(null, Validators.required),
    },
    { validators: [this.visibleToRequiredWhenShared()] },
  );
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public tenants$ = this.tenantService.getAll$();

  @Input()
  public set dashboard(value: IDashboardListDto) {
    if (!value?.id) {
      return;
    }

    this.isLoading$.next(true);
    this.service
      .getByIdWithTranslations$(value.id)
      .pipe(take(1))
      .subscribe({
        next: (dashboard) => {
          this.form.reset(
            {
              id: dashboard.id,
              name: dashboard.name,
              description: dashboard.description,
              color: dashboard.color ?? DEFAULT_DASHBOARD_COLOR,
              shared: dashboard.shared,
              visibleTo: dashboard.visibleTo,
              tenant: dashboard.tenant,
            },
            { emitEvent: true },
          );
          this.isLoading$.next(false);
        },
        error: () => {
          this.isLoading$.next(false);
        },
      });
  }

  get isShared(): boolean {
    return this.form.get('shared')?.value === true;
  }

  get tenant(): AbstractControl | null {
    return this.form.get('tenant');
  }

  private visibleToRequiredWhenShared(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const form = control as UntypedFormGroup;

      if (!form.get('shared')?.value) {
        return null;
      }

      const visibleTo = form.get('visibleTo')?.value;

      if (
        visibleTo == null ||
        (Array.isArray(visibleTo) && visibleTo.length === 0)
      ) {
        return { visibleToRequired: true };
      }

      return null;
    };
  }

  private requiredTranslationValues(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const localizations = control.value?.localizations;

      if (!Array.isArray(localizations) || localizations.length === 0) {
        return { requiredTranslation: true };
      }

      const hasMissingValue = localizations.some(
        (localization: { value?: string | null }) =>
          !localization.value?.trim(),
      );

      return hasMissingValue ? { requiredTranslation: true } : null;
    };
  }
}
